/**
 * ⑭ R14a — 탐색 v2 구조의 단일 출처 (NAV14_20260929 「R14a 탐색」).
 *
 * RD(2026-10-11, 화면 예산제 §3-1) — 탭 5 = 홈·부동산·블로그·게시판·주식. 모바일 하단 탭과 데스크톱 상단이
 * «이 배열 하나» 를 읽는다(데스크톱 = 홈을 뺀 4). 하위 링크 합계 ≤ 12(G1), 더보기 없음.
 * 두 벌로 적으면 한쪽만 바뀐다(옛 Navigation 의 NAV_ITEMS/MOBILE_TABS 가 그렇게 갈렸다).
 * /more 라우트와 MORE_GROUPS 는 «메뉴에서만» 빠졌다 — URL 삭제 0, 페이지는 그대로 산다.
 *
 * ⛔ 새 경로를 만들지 않는다. 모든 href 는 이미 있는 라우트다(테스트가 app 디렉터리와 대조한다).
 * ⛔ 라벨 3종은 APT_LABEL 한 곳 — 메뉴·탭·칩·빵부스러기가 같은 말을 쓴다.
 * ⛔ 이 파일은 순수 데이터다(훅·DB·브라우저 API 없음). 서버·클라이언트 어디서나 import 가능.
 */
import { KAKAO_TALK_URL } from '@/lib/talk-banner';
import { CAFE_BASE_URL } from '@/lib/cafe/contract';
import { isHomePath } from '@/lib/route/is-home';

/** 라벨 3종 통일(⑭ 규칙) — 메뉴·탭·칩·빵부스러기·title 꼬리가 같은 말을 쓴다. */
export const APT_LABEL = {
  subscription: '청약·분양예정',
  redev: '재개발·재건축',
  unsold: '미분양',
} as const;

/** CL(2026-10-15 §4-4) — 모바일 하단 탭 5 = 홈 · 분양 · 지역 · 글 · 카페. */
export type NavTabKey = 'home' | 'apt' | 'region' | 'blog' | 'cafe';

/** CL — 데스크톱 상단 메뉴 6 = 분양 · 청약 일정 · 재개발·재건축 · 지역 · 분석 글 · 도구(+ 검색 자리). */
export type TopMenuKey = 'apt' | 'calendar' | 'redev' | 'region' | 'blog' | 'tools';

export interface NavLink {
  /** 계측 id(짧은 영문). nav_click·more_click 의 event_name. */
  id: string;
  label: string;
  href: string;
  /** /more·드롭다운의 한 줄 보조 설명. */
  sub?: string;
  /** 외부 링크(카톡방). */
  external?: boolean;
  /** 색인 제외 화면(광고 신청·내 광고·광고 약관)으로 가는 링크 — rel="nofollow". */
  nofollow?: boolean;
  /**
   * retention-1031 — 수화 뒤 지역 칩(GlobalRegionChip)이 현재 지역으로 갈아끼우는 링크 종류(data-kd-region-link).
   *   서버 HTML 은 href 그대로(방문자 무관 · 같은 URL = 같은 HTML).
   */
  regionLink?: 'lead';
}

export interface NavTab {
  key: NavTabKey;
  label: string;
  href: string;
  /** 2층(데스크톱 드롭다운). 없으면 드롭다운 없이 링크만. */
  children?: readonly NavLink[];
  /** 외부 링크(카페) — 새 창. */
  external?: boolean;
}

export interface TopMenuItem {
  key: TopMenuKey;
  label: string;
  href: string;
}

/** 청약 캘린더 — 전용 라우트가 없다(/apt/big-events 는 재건축 «이벤트» 목록·noindex). /apt 의 「지금 청약」 절(#now)로 보낸다. */
export const APT_CALENDAR_HREF = '/apt#now';

/** 관심고객 등록 — 독립 라우트가 없다. 지역 허브 리드폼(부산 · 부울경 우선) 앵커로 보낸다.
 *  ⚠️ 이 값은 «서버 HTML 기본값» 이다. 수화 뒤에는 현재 지역(경로 > 상세 현장 지역 > 기억)의 허브 폼으로 바뀐다
 *     (regionLink 'lead' · region-scope leadRegisterHrefFor · retention-1031). 지역을 모르면 이 값 그대로. */
export const LEAD_REGISTER_HREF = `/apt/region/${encodeURIComponent('부산')}#lead-form`;

/** 메뉴에서 빠진 링크 — /more 가 «하나도 지우지 않고» 계속 받는다(테스트가 보존을 잠근다). */
const MENU_DROPPED: readonly NavLink[] = [
  { id: 'apt_calendar', label: '청약 캘린더', href: APT_CALENDAR_HREF },
  { id: 'apt_diagnose', label: '가점 진단', href: '/apt/diagnose' },
  { id: 'apt_data', label: '부동산 통계', href: '/apt/data' },
  { id: 'board_ask', label: '질문 올리기', href: '/board/write?cat=qa' },
  { id: 'blog_press', label: '보도자료', href: '/press' },
  { id: 'stock_themes', label: '테마주', href: '/stock/themes' },
  { id: 'stock_compare', label: '종목 비교', href: '/stock/compare' },
];

export interface MoreGroup {
  key: MoreGroupKey;
  title: string;
  /** 묶음마다 1줄 설명(⑭ 규칙). */
  desc: string;
  items: readonly NavLink[];
  /** 설정 묶음의 「글자 크기」처럼 링크가 아닌 조작. 화면이 그린다(/more 만 — 헤더 더보기 패널은 링크만). */
  controls?: readonly ('font-size')[];
}

export type MoreGroupKey = 'ad' | 'presale' | 'region' | 'community' | 'stock' | 'me' | 'help';

/** 광고 묶음 — 광고주 진입. 출발 지면 표지 from=more(광고 페이지·신청서가 출처로 읽는다). */
export const MORE_AD_FROM = 'more';

/**
 * more-tidy — 더보기 7묶음(모바일 /more · PC 헤더 「더보기」 패널이 같은 표를 읽는다).
 *   광고(맨 위 · 광고주 모집) → 분양·청약 → 지역·시세 → 글·커뮤니티 → 주식 → 내 정보·설정 → 고객 안내.
 *
 * 걷어낸 항목(라우트·리다이렉트는 그대로 — 메뉴에서만 뺐다):
 *   /stock/dividend · /stock/compare → 301 /stock(종목 항목과 중복) · /glossary → 301 /blog · /daily → 301 /apt/region(지역 항목과 중복)
 *   · /discuss → 301 /blog · /consultant(「준비 중」 빈 화면 · noindex) · /search(헤더 검색과 중복).
 * ⛔ /blog 는 넣지 않는다(하단 탭 「블로그」와 중복 — nav-live 테스트). 카페 링크는 넣지 않는다(헤더 HTML 에 cafe 0 — nav 테스트).
 */
export const MORE_GROUPS: readonly MoreGroup[] = [
  {
    key: 'ad',
    title: '광고',
    desc: '분양 현장·지역 페이지에 광고를 싣고 관리합니다.',
    items: [
      { id: 'ad_info', label: '광고하기', href: `/advertise?from=${MORE_AD_FROM}`, sub: '상품·지면 안내' },
      { id: 'ad_buy', label: '광고 신청', href: `/ads/buy?from=${MORE_AD_FROM}`, sub: '바로 신청·결제', nofollow: true },
      { id: 'ad_quote', label: '견적서 받기', href: `/advertise?from=${MORE_AD_FROM}#quote`, sub: '조건별 견적 요청' },
      { id: 'ad_my', label: '내 광고', href: '/ads/my', sub: '상태·소재·연장', nofollow: true },
      { id: 'ad_terms', label: '광고 약관', href: '/ads/terms', sub: '약관·환불 규정', nofollow: true },
    ],
  },
  {
    key: 'presale',
    title: '분양·청약',
    desc: '청약 일정과 분양예정·정비사업 현장을 봅니다.',
    items: [
      { id: 'apt_subscription', label: APT_LABEL.subscription, href: '/apt', sub: '지금 청약 · 곧 나올 현장' },
      { id: 'apt_calendar', label: '청약 일정', href: '/calendar', sub: '접수·발표 달력' },
      { id: 'tool_redev', label: APT_LABEL.redev, href: '/apt/redev', sub: '정비사업 현황' },
      { id: 'apt_unsold', label: APT_LABEL.unsold, href: '/apt/unsold', sub: '시도별 미분양 현황' },
      { id: 'tool_diagnose', label: '가점 진단', href: '/apt/diagnose', sub: '청약 가점 계산' },
      { id: 'join_lead', label: '관심고객 등록', href: LEAD_REGISTER_HREF, sub: '분양 소식 먼저 받기', regionLink: 'lead' },
    ],
  },
  {
    key: 'region',
    title: '지역·시세',
    desc: '지역별 분양과 단지 시세·실거래를 찾습니다.',
    items: [
      { id: 'apt_region', label: '지역별 분양', href: '/apt/region', sub: '시도·시군구 허브' },
      { id: 'tool_complex', label: '단지백과', href: '/apt/complex', sub: '전국 단지 시세' },
      { id: 'tool_apt_search', label: '실거래 검색', href: '/apt/search', sub: '전국 실거래가' },
      { id: 'tool_apt_data', label: '부동산 통계', href: '/apt/data', sub: '공공 데이터' },
      { id: 'tool_calc', label: '계산기', href: '/calc', sub: '부동산·세금 계산' },
    ],
  },
  {
    key: 'community',
    title: '글·커뮤니티',
    desc: '연재 글을 읽고 이야기를 나눕니다.',
    items: [
      { id: 'tool_series', label: '시리즈', href: '/blog/series', sub: '주제별 연재 모음' },
      { id: 'join_board', label: '게시판', href: '/board', sub: '현장 토론방 · 질문답변 · 자유글' },
      { id: 'join_talk', label: '카톡방', href: KAKAO_TALK_URL, sub: '부정공 단톡방 입장', external: true },
    ],
  },
  {
    key: 'stock',
    title: '주식',
    desc: '종목 시세와 테마·등락을 봅니다.',
    items: [
      { id: 'stock_home', label: '종목 시세', href: '/stock', sub: '국내·해외 시세' },
      { id: 'stock_themes', label: '테마주', href: '/stock/themes', sub: '섹터별 관련주' },
      { id: 'stock_movers', label: '급등락', href: '/stock/movers', sub: '등락률 상위' },
      { id: 'stock_search', label: '종목 검색', href: '/stock/search', sub: '지표로 걸러보기' },
      { id: 'stock_data', label: '주식 통계', href: '/stock/data', sub: '시장 데이터' },
    ],
  },
  {
    key: 'me',
    title: '내 정보·설정',
    desc: '지역·알림·글자 크기를 정합니다.',
    controls: ['font-size'],
    items: [
      { id: 'set_region', label: '지역 설정', href: '/settings/region', sub: '우리동네 설정·변경' },
      { id: 'set_notify', label: '알림 설정', href: '/notifications/settings', sub: '푸시·이메일·카카오' },
      { id: 'set_interests', label: '관심사 설정', href: '/settings/interests', sub: '맞춤 알림 주제' },
    ],
  },
  {
    key: 'help',
    title: '고객 안내',
    desc: '이용 방법과 약관·개인정보 처리 기준을 안내합니다.',
    items: [
      { id: 'set_guide', label: '가이드북', href: '/guide', sub: '이용 방법 안내' },
      { id: 'set_install', label: '앱 설치', href: '/guide#install', sub: '홈 화면에 추가' },
      { id: 'set_faq', label: 'FAQ', href: '/faq', sub: '자주 묻는 질문' },
      { id: 'set_about', label: '서비스 소개', href: '/about', sub: '카더라 소개' },
      { id: 'tool_press', label: '보도자료', href: '/press', sub: '미디어 키트' },
      { id: 'help_terms', label: '이용약관', href: '/terms', sub: '서비스 이용 조건' },
      { id: 'help_privacy', label: '개인정보처리방침', href: '/privacy', sub: '개인정보 처리 기준' },
    ],
  },
];

/** 네이버 카페(부정공) — 본체 밖 «공식 카페» 링크. ⛔ Hostinger 위성 링크 아님. */
/** hc3 H6 — 카페 주소 단일 출처(cafe/contract CAFE_BASE_URL, 값 불변). */
export const NAV_CAFE_URL = CAFE_BASE_URL;

/** 도구 착지 — 레인 C 「도구」 착지 페이지가 정해지면 그 경로로 바꾼다(기본값 /calc · 실재 라우트). */
export const TOOLS_HREF = '/calc';

/** CL §4-4 — 모바일 하단 탭 5(홈 · 분양 · 지역 · 글 · 카페). 정적 a href. */
export const NAV_TABS: readonly NavTab[] = [
  { key: 'home', label: '홈', href: '/' },
  { key: 'apt', label: '분양', href: '/apt' },
  { key: 'region', label: '지역', href: '/apt/region' },
  { key: 'blog', label: '글', href: '/blog' },
  { key: 'cafe', label: '카페', href: NAV_CAFE_URL, external: true },
];

/** CL §4-4 — 데스크톱 상단 메뉴 6(드롭다운 없음 · 사이트링크 대비 정적 a href). 주식·피드·토론 메뉴 없음. */
export const TOP_MENU: readonly TopMenuItem[] = [
  { key: 'apt', label: '분양', href: '/apt' },
  { key: 'calendar', label: '청약 일정', href: '/calendar' },
  { key: 'redev', label: APT_LABEL.redev, href: '/apt/redev' },
  { key: 'region', label: '지역', href: '/apt/region' },
  { key: 'blog', label: '분석 글', href: '/blog' },
  { key: 'tools', label: '도구', href: TOOLS_HREF },
];

const under = (p: string, base: string) => p === base || p.startsWith(`${base}/`);
const isRegionPath = (p: string) => under(p, '/apt/region') || under(p, '/apt/area');

/** 경로 → 활성 하단 탭. 방문자와 무관(경로만). 소속 탭이 없으면 null. */
export function activeTabOf(pathname: string | null | undefined): NavTabKey | null {
  const p = pathname || '';
  if (isHomePath(p)) return 'home';
  if (isRegionPath(p)) return 'region';
  if (under(p, '/apt')) return 'apt';
  if (under(p, '/blog')) return 'blog';
  return null;
}

/** 경로 → 활성 상단 메뉴. */
export function activeTopOf(pathname: string | null | undefined): TopMenuKey | null {
  const p = pathname || '';
  if (under(p, '/calendar')) return 'calendar';
  if (under(p, '/apt/redev')) return 'redev';
  if (isRegionPath(p)) return 'region';
  if (under(p, '/apt')) return 'apt';
  if (under(p, '/blog')) return 'blog';
  if (under(p, TOOLS_HREF)) return 'tools';
  return null;
}

/** 탭·드롭다운 전체 링크(테스트·감사용). */
export function allNavHrefs(): string[] {
  const out = new Set<string>();
  for (const t of NAV_TABS) {
    out.add(t.href);
    for (const c of t.children ?? []) out.add(c.href);
  }
  for (const m of TOP_MENU) out.add(m.href);
  for (const c of MENU_DROPPED) out.add(c.href);
  for (const g of MORE_GROUPS) for (const i of g.items) out.add(i.href);
  return [...out];
}
